// Single home for the "documents per order" read model of the Dokumenty tab.
//
// Every Document Center row is attached to at most one order: a customer
// order (WZ, sales invoice) or a supplier order (PZ via its source order,
// purchase invoice). Rows without either land in a trailing "no order"
// group. Groups are ordered by their newest document so recent work stays on
// top; documents inside a group keep the backend order (newest first).
import type { DocumentCenterItem } from './api/documentCenterApi';

export type DocumentCenterGroupKind = 'CUSTOMER_ORDER' | 'SUPPLIER_ORDER' | 'NONE';

export interface DocumentCenterGroup {
  key: string;
  kind: DocumentCenterGroupKind;
  /** Route of the order the group belongs to; null for the no-order group. */
  orderPath: string | null;
  /** Operator-facing order number; null for the no-order group. */
  orderNumber: string | null;
  clientOrderNumber: string | null;
  /** Customer (customer order) or supplier (supplier order) name. */
  partyName: string | null;
  documents: DocumentCenterItem[];
  /** Newest document date in the group, ISO string or null when unknown. */
  latestDate: string | null;
}

const NO_ORDER_KEY = 'NONE';

const documentTime = (document: DocumentCenterItem): number => {
  const raw = document.date || document.createdAt;
  const time = raw ? new Date(raw).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
};

const resolveGroupSeed = (document: DocumentCenterItem): Pick<DocumentCenterGroup, 'key' | 'kind' | 'orderPath' | 'orderNumber' | 'clientOrderNumber' | 'partyName'> => {
  if (document.orderId) {
    return {
      key: `CUSTOMER_ORDER:${document.orderId}`,
      kind: 'CUSTOMER_ORDER',
      orderPath: `/app/orders/${document.orderId}`,
      orderNumber: document.displayOrderNumber || document.orderNumber || document.clientOrderNumber || document.orderId,
      clientOrderNumber: document.clientOrderNumber || null,
      partyName: document.customerName || null,
    };
  }
  if (document.supplierOrderId) {
    return {
      key: `SUPPLIER_ORDER:${document.supplierOrderId}`,
      kind: 'SUPPLIER_ORDER',
      orderPath: `/app/supplier-orders/${document.supplierOrderId}`,
      orderNumber: document.supplierOrderNumber || document.displayOrderNumber || document.orderNumber || document.supplierOrderId,
      clientOrderNumber: document.clientOrderNumber || null,
      partyName: document.supplierName || null,
    };
  }
  return {
    key: NO_ORDER_KEY,
    kind: 'NONE',
    orderPath: null,
    orderNumber: null,
    clientOrderNumber: null,
    partyName: null,
  };
};

export function groupDocumentsByOrder(documents: DocumentCenterItem[]): DocumentCenterGroup[] {
  const groups = new Map<string, DocumentCenterGroup>();

  documents.forEach((document) => {
    const seed = resolveGroupSeed(document);
    const existing = groups.get(seed.key);
    if (existing) {
      existing.documents.push(document);
      // Fill party/order labels from a later row when the first row lacked them.
      if (!existing.partyName && seed.partyName) existing.partyName = seed.partyName;
      if (!existing.clientOrderNumber && seed.clientOrderNumber) existing.clientOrderNumber = seed.clientOrderNumber;
      return;
    }
    groups.set(seed.key, { ...seed, documents: [document], latestDate: null });
  });

  const result = Array.from(groups.values()).map((group) => {
    const newest = group.documents.reduce((max, document) => Math.max(max, documentTime(document)), 0);
    return { ...group, latestDate: newest ? new Date(newest).toISOString() : null };
  });

  return result.sort((a, b) => {
    if (a.kind === 'NONE') return 1;
    if (b.kind === 'NONE') return -1;
    const byDate = (b.latestDate ? new Date(b.latestDate).getTime() : 0) - (a.latestDate ? new Date(a.latestDate).getTime() : 0);
    if (byDate !== 0) return byDate;
    return (a.orderNumber || '').localeCompare(b.orderNumber || '', 'pl');
  });
}
